import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSessionCustomers, getSessionCustomersEnhanced } from "../../services/adminTimeTableServices";
import { sendBulkWhatsAppMessage } from "../../services/adminUserService";
import "./EnhancedSessionCustomers.css";

const EnhancedSessionCustomersPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      
      // Try enhanced API first, fallback to regular API
      let res;
      let usingEnhancedAPI = false;
      
      try {
        res = await getSessionCustomersEnhanced(sessionId);
        usingEnhancedAPI = true;
      } catch (enhancedError) {
        // Fallback to regular API and transform data
        res = await getSessionCustomers(sessionId);
        
        // Transform regular API response to enhanced format
        if (res.data && res.data.customers) {
          res.data = {
            ...res.data,
            sessionId: parseInt(sessionId),
            customers: res.data.customers.map(customer => ({
              ...customer,
              whatsappNumber: customer.whatsappNumber || null,
              whatsappConsent: customer.whatsappConsent || false,
              planName: customer.planName || null,
              availableSlots: customer.availableSlots || null
            }))
          };
        }
      }
      
      setData(res.data);
    } catch (err) {
      console.error("Failed to load session customers:", err);
      alert(`Failed to load session customers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      
      // Show temporary feedback
      const button = document.activeElement;
      const originalText = button.innerHTML;
      button.innerHTML = "✓ Copied!";
      button.style.background = "#4CAF50";
      
      setTimeout(() => {
        button.innerHTML = originalText;
        button.style.background = "";
      }, 1000);
      
    } catch (err) {
      console.error(`Failed to copy ${type}:`, err);
      alert(`Failed to copy ${type}`);
    }
  };

  const formatWhatsAppNumber = (number) => {
    if (!number) return "Not provided";
    const cleanNumber = number.replace(/\\+/g, '');
    if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
      return `+91 ${cleanNumber.slice(2, 7)}-${cleanNumber.slice(7)}`;
    }
    return number.startsWith('+') ? number : `+${number}`;
  };

  const toggleCustomerSelection = (customer) => {
    setSelectedCustomers(prev => {
      const exists = prev.find(c => c.userId === customer.userId);
      if (exists) {
        return prev.filter(c => c.userId !== customer.userId);
      } else {
        return [...prev, customer];
      }
    });
  };

  const selectAllWithWhatsApp = () => {
    const whatsappCustomers = data.customers.filter(c => 
      c.whatsappNumber && c.whatsappConsent && c.status === "BOOKED"
    );
    setSelectedCustomers(whatsappCustomers);
  };

  const clearSelection = () => {
    setSelectedCustomers([]);
  };

  const handleBulkWhatsApp = async () => {
    if (!bulkMessage.trim()) {
      alert("Please enter a message");
      return;
    }

    setSendingBulk(true);
    try {
      const userIds = selectedCustomers.map(c => c.userId);
      
      // Check if bulk WhatsApp API is available
      try {
        await sendBulkWhatsAppMessage(userIds, bulkMessage);
        alert(`WhatsApp message sent to ${selectedCustomers.length} customers!`);
      } catch (apiError) {
        // If bulk API not available, show message with user IDs for manual processing
        const eligibleCustomers = selectedCustomers.filter(c => c.whatsappNumber && c.whatsappConsent);
        const userIdsList = eligibleCustomers
          .map(c => `${c.name} (ID: ${c.userId}, WhatsApp: ${c.whatsappNumber})`)
          .join('\n');
        
        alert(`Bulk WhatsApp API not yet available.\n\nSelected customers:\n${userIdsList}\n\nMessage:\n${bulkMessage}`);
      }
      
      setShowBulkModal(false);
      setBulkMessage("");
      setSelectedCustomers([]);
    } catch (error) {
      console.error("Failed to send bulk WhatsApp:", error);
      alert("Failed to send bulk messages");
    } finally {
      setSendingBulk(false);
    }
  };

  const downloadEnhancedCSV = () => {
    if (!data?.customers?.length) {
      alert("No customers to download");
      return;
    }

    const headers = [
      "Name", "Email", "WhatsApp Number", "WhatsApp Consent", 
      "Status", "Booked At", "Plan"
    ];

    const rows = data.customers.map((c) => [
      c.name || "Unknown",
      c.email || "N/A",
      c.whatsappNumber || "Not provided",
      c.whatsappConsent ? "Yes" : "No",
      c.status,
      new Date(c.bookedAt).toLocaleString(),
      c.planName || "N/A"
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
      )
      .join("\\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `session_${sessionId}_customers_enhanced.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading customers...</div>;
  }

  if (!data) {
    return <p>No data found.</p>;
  }

  if (!Array.isArray(data.customers)) {
    return <p>Invalid customer data</p>;
  }

  const whatsappEnabledCount = data.customers.filter(c => 
    c.whatsappNumber && c.whatsappConsent && c.status === "BOOKED"
  ).length;

  return (
    <div className="enhanced-session-customers">
      <div className="page-header">
        <div className="header-actions">
          <button className="btn-back" onClick={() => navigate(-1)}>
            ⬅ Back
          </button>
          <button className="btn-download" onClick={downloadEnhancedCSV}>
            ⬇ Download CSV
          </button>
        </div>
        
        <h2>👥 Session Customers</h2>
        
        {selectedCustomers.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedCustomers.length} customers selected</span>
            <button 
              className="btn-bulk-whatsapp"
              onClick={() => setShowBulkModal(true)}
              disabled={selectedCustomers.filter(c => c.whatsappNumber && c.whatsappConsent).length === 0}
            >
              📱 Send Bulk WhatsApp
            </button>
            <button className="btn-clear" onClick={clearSelection}>
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Summary */}
      <div className="summary-cards">
        <div className="summary-card">
          <h4>Total Booked</h4>
          <p>{data.totalBooked}</p>
        </div>
        <div className="summary-card">
          <h4>Cancelled</h4>
          <p>{data.totalCancelled}</p>
        </div>
        <div className="summary-card">
          <h4>WhatsApp Enabled</h4>
          <p>{whatsappEnabledCount}</p>
        </div>
        <div className="summary-card">
          <h4>Session ID</h4>
          <p>{data.sessionId}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <button 
          className="btn-select-all"
          onClick={selectAllWithWhatsApp}
          disabled={whatsappEnabledCount === 0}
        >
          Select All WhatsApp Users ({whatsappEnabledCount})
        </button>
      </div>

      {/* Enhanced Table */}
      <div className="table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectAllWithWhatsApp();
                    } else {
                      clearSelection();
                    }
                  }}
                  checked={selectedCustomers.length === whatsappEnabledCount && whatsappEnabledCount > 0}
                />
              </th>
              <th className="name-column">Name</th>
              <th className="email-column">Email</th>
              <th className="whatsapp-column">WhatsApp Number</th>
              <th className="consent-column">Consent</th>
              <th className="plan-column">Plan</th>
              <th className="status-column">Status</th>
              <th className="date-column">Booked At</th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((customer, index) => (
              <tr key={customer.userId ?? index} className={selectedCustomers.find(c => c.userId === customer.userId) ? 'selected' : ''}>
                <td className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.find(c => c.userId === customer.userId) !== undefined}
                    onChange={() => toggleCustomerSelection(customer)}
                    disabled={!customer.whatsappNumber || !customer.whatsappConsent || customer.status !== "BOOKED"}
                  />
                </td>
                <td className="name-column">
                  <div className="name-content">
                    <span className="customer-name">{customer.name || "Unknown"}</span>
                  </div>
                </td>
                <td className="email-column">
                  <div className="email-content">
                    <span className="email-text">{customer.email || "N/A"}</span>
                    {customer.email && (
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(customer.email, 'email')}
                        title="Copy email"
                      >
                        📋
                      </button>
                    )}
                  </div>
                </td>
                <td className="whatsapp-column">
                  <div className="whatsapp-content">
                    <span className="whatsapp-text">{formatWhatsAppNumber(customer.whatsappNumber)}</span>
                    {customer.whatsappNumber && (
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(customer.whatsappNumber, 'WhatsApp number')}
                        title="Copy WhatsApp number"
                      >
                        📋
                      </button>
                    )}
                  </div>
                </td>
                <td className="consent-column">
                  <span className={`consent-badge ${customer.whatsappConsent ? 'consented' : 'not-consented'}`}>
                    {customer.whatsappConsent ? "✅ Yes" : "❌ No"}
                  </span>
                </td>
                <td className="plan-column">
                  <span className="plan-text">{customer.planName || "N/A"}</span>
                </td>
                <td className="status-column">
                  <span className={`status-badge ${customer.status.toLowerCase()}`}>
                    {customer.status}
                  </span>
                </td>
                <td className="date-column">
                  <span className="date-text">
                    {customer.bookedAt
                      ? new Date(customer.bookedAt).toLocaleDateString() + " " + new Date(customer.bookedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                      : "N/A"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk WhatsApp Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="bulk-whatsapp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Bulk WhatsApp Message</h3>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              <p>Sending to {selectedCustomers.filter(c => c.whatsappNumber && c.whatsappConsent).length} customers:</p>
              
              <div className="recipients-list">
                {selectedCustomers
                  .filter(c => c.whatsappNumber && c.whatsappConsent)
                  .slice(0, 5)
                  .map(c => (
                    <span key={c.userId} className="recipient-tag">
                      {c.name} ({formatWhatsAppNumber(c.whatsappNumber)})
                    </span>
                  ))}
                {selectedCustomers.filter(c => c.whatsappNumber && c.whatsappConsent).length > 5 && (
                  <span className="more-recipients">
                    +{selectedCustomers.filter(c => c.whatsappNumber && c.whatsappConsent).length - 5} more
                  </span>
                )}
              </div>

              <textarea
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                maxLength={1000}
                disabled={sendingBulk}
                className="bulk-message-input"
              />
              <div className="char-count">{bulkMessage.length}/1000 characters</div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowBulkModal(false)} disabled={sendingBulk}>
                Cancel
              </button>
              <button 
                className="btn-send" 
                onClick={handleBulkWhatsApp} 
                disabled={sendingBulk || !bulkMessage.trim()}
              >
                {sendingBulk ? "Sending..." : "Send Messages"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSessionCustomersPage;